import { useState, useEffect, useRef, useContext } from 'react';
import classNames from 'classnames/bind';
import styles from './AdminMessagePage.module.scss';
import {
  convertMessageDataToFormData,
  isUserIdExists,
  isUserInMessageList,
  normalizeMessages,
  normalizeUserData,
} from '~/helper/help';
import { getData, postData } from '~/helper/apiService';
import { LoginContext } from '~/component/Context/LoginProvider';
import { FaAlignLeft, FaAngleLeft } from 'react-icons/fa';

const cx = classNames.bind(styles);

function AdminMessagePage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const [newMessageList, setNewMessageList] = useState([]);
  const { loginStatus } = useContext(LoginContext);
  const [isShowSidebar, setIsShowSidebar] = useState(true);
  // Fetch users and initialize
  const fetchUsers = async () => {
    const response = await getData('/user/customer');
    setUsers(normalizeUserData(response));
  };

  useEffect(() => {
    if (loginStatus) {
      fetchUsers();
      getUserNotSeen();
    }
  }, [loginStatus]);

  // Fetch messages when a user is selected
  useEffect(() => {
    if (selectedUser) {
      // ẩn sidebar
      setIsShowSidebar(false);
      // set seen cho tất cả tin nhắn
      setSeenMessage(selectedUser.id);

      const fetchMessages = async () => {
        const response = await getData(`/message/customer/${selectedUser.id}`);
        if (response && response.length > 0) {
          if (Array.isArray(response)) {
            const data = normalizeMessages(response);
            setConversationId(data[0].conversation_id);
            setMessages(data || []);
            // Xóa các tin nhắn mới của user khỏi danh sách
            setNewMessageList((prev) => prev.filter((message) => message.sender_id !== selectedUser.id));
          } else {
            setMessages([]);
            console.warn('⚠️ Không phải mảng:', response);
          }
        }
      };
      fetchMessages();
    }
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Lắng nghe tin nhắn
  useEffect(() => {
    const handleIncomingMessage = (message) => {
      if (selectedUser) {
        // Chỉ xử lý nếu người nhận là user đang chọn
        if (message.sender_id === selectedUser.id) {
          setMessages((prev) => [...prev, message]);
        } else {
          // '🚫 Chưa chọn người dùng này, bỏ qua');
          setNewMessageList((prev) => [...prev, message]);
        }
      } else {
        setNewMessageList((prev) => [...prev, message]);
      }

      // nếu mà đây là tin nhắn từ người dùng mới, thì thêm vào list
      if (!isUserIdExists(users, message.sender_id)) {
        fetchUsers();
      }
    };
  }, [selectedUser]);

  // hàm kiểm tra xem là đã user đã được seen hay chưa
  async function getUserNotSeen() {
    const response = await getData('/message/latest');
    if (response) {
      const data = normalizeMessages(response);
      setNewMessageList(data);
    }
  }
  // hàm seen message

  const handleSendMessage = async () => {
    if (inputValue.trim() && selectedUser) {
      const messageData = {
        conversation_id: conversationId || null, // Will be set after first message
        sender_id: 1,
        receiver_id: selectedUser.id,
        content: inputValue,
        message_type: 'text',
        timestamp: new Date().toISOString(),
        seen: false,
        is_edited: false,
        is_deleted: false,
        reply_to_id: null,
      };
      const formData = convertMessageDataToFormData(messageData);

      const response = await postData('/message/send', formData);

      if (response.status === 'success') {
        setMessages([...messages, messageData]);
        setInputValue('');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  async function setSeenMessage(userId) {
    await getData(`/message/seenmessage/${userId}`);
  }

  const handleTogleSidebar = () => {
    setIsShowSidebar((prev) => !prev);
  };

  if (!loginStatus) return 'Vui lòng đăng nhập để sử dụng chức năng này !';

  return (
    <div className={cx('wrapper')}>
      <div className={cx('sidebar', { hide: !isShowSidebar })}>
        <h2 className={cx('sidebar-title')}>Users</h2>
        <div className={cx('user-list')}>
          {users.map((user) => (
            <div
              key={user.id}
              className={cx('user-item', {
                selected: selectedUser?.id === user.id,
                new: isUserInMessageList(user, newMessageList),
              })}
              onClick={() => setSelectedUser(user)}
            >
              <span className={cx('user-name')}>{user.name}</span>
              <span className={cx('user-role')}>{user.role}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={cx('chat-area')}>
        {selectedUser ? (
          <>
            <div className={cx('chat-header')}>
              <FaAngleLeft className={cx('icon')} onClick={handleTogleSidebar} />
              <h3>{selectedUser.name}</h3>
              <span className={cx('user-status', selectedUser.status)}>{selectedUser.status}</span>
            </div>
            <div className={cx('message-list')}>
              {messages.map((message, index) => (
                <div key={index} className={cx('message', message.sender_id === 1 ? 'sent' : 'received')}>
                  <div className={cx('message-content')}>{message.content}</div>
                  <div className={cx('message-timestamp')}>
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className={cx('message-input-container')}>
              <textarea
                className={cx('message-input')}
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              ></textarea>
              <button className={cx('send-button')} onClick={handleSendMessage}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </>
        ) : (
          <div className={cx('no-selection')}>Select a user to start chatting</div>
        )}
      </div>
    </div>
  );
}

export default AdminMessagePage;
